import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1707430393809-784967fe6fee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBncmVlbiUyMGdvbGQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NjgxMzUwN3ww&ixlib=rb-4.1.0&q=85";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.14, delayChildren: 0.3 } } };
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } }
};

export const Hero = ({ totals, onNavigate }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const txtY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const { t } = useLang();

  const labels = t("hero.stats");
  const stats = [
    { value: totals.concepts ?? "41", label: labels[0] },
    { value: totals.sectors ?? "20", label: labels[1] },
    { value: totals.confirmed ?? "12", label: labels[2] },
    { value: `${totals.prototypeDays ?? 21}`, label: labels[3] }
  ];
  const lines = [
    { text: t("hero.h1a"), cls: "text-cream" },
    { text: t("hero.h1b"), cls: "text-gold italic" }
  ];

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen bg-ink overflow-hidden flex flex-col grain">
      <motion.div style={{ y: imgY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <img src={HERO_IMG} alt="" className="absolute right-[-10%] top-[8%] w-[70%] max-w-[900px] opacity-25 mix-blend-screen rtl:left-[-10%] rtl:right-auto" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/70 to-transparent rtl:bg-gradient-to-l" />
      </motion.div>

      <motion.div style={{ y: txtY }} className="relative z-10 flex-1 flex flex-col justify-center max-w-[1400px] w-full mx-auto px-6 md:px-10 pt-36 pb-16">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }}
          data-testid="hero-eyebrow"
          className="text-gold text-[11px] md:text-xs tracking-mega uppercase mb-8">
          {t("hero.eyebrow")}
        </motion.p>

        <motion.h1
          variants={container} initial="hidden" animate="show" key={lines[0].text}
          data-testid="hero-heading"
          className="font-display text-[13vw] sm:text-6xl lg:text-[86px] leading-[1.15] max-w-5xl">
          {lines.map((l, i) => (
            <span key={i} className="mask-line pb-1">
              <motion.span variants={line} className={`block ${l.cls}`}>{l.text}</motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.8, ease: "easeOut" }}
          data-testid="hero-subcopy"
          className="mt-8 max-w-xl text-cream/70 text-sm md:text-base leading-relaxed">
          {t("hero.sub1")} <span className="text-cream">{t("hero.subBiz")}</span> {t("hero.subMid")}{" "}
          <span className="text-cream">{t("hero.subInd")}</span> {t("hero.subEnd")}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8, ease: "easeOut" }}
          className="mt-10 flex flex-wrap items-center gap-5">
          <button data-testid="hero-cta-primary" onClick={() => onNavigate("library")}
            className="group flex items-center gap-3 bg-gold text-ink text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-cream transition-colors duration-300">
            {t("hero.cta1")}
            <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <button data-testid="hero-cta-secondary" onClick={() => onNavigate("matcher")}
            className="group flex items-center gap-3 border border-gold/60 text-gold text-xs tracking-[0.2em] uppercase px-8 py-4 hover:border-gold hover:bg-gold/10 transition-[background-color,border-color] duration-300">
            {t("hero.cta2")}
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
        data-testid="hero-stats"
        className="relative z-10 border-t border-gold/25">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
          {stats.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}
              className={`py-8 md:py-10 ${i > 0 ? "md:border-s md:border-gold/25 md:ps-8" : ""} ${i % 2 === 1 ? "border-s border-gold/25 ps-6 md:ps-8" : ""}`}>
              <div className="font-display text-3xl md:text-4xl text-gold">{s.value}</div>
              <div className="mt-2 text-cream/50 text-[11px] tracking-[0.25em] uppercase">{s.label}</div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
